import { useEffect, useId, useState, type ReactNode } from 'react';
import {
  ACCESS_ROLE_LABELS,
  AccessEndPicker,
  accessEndIso,
  Button,
  defaultAccessEnd,
  FUNNEL_STAGES,
  FUNNEL_STAGE_LABELS,
  formatDate,
  HelpTip,
} from '@dataroom/shared';
import type {
  AccessEndValue,
  AccessGrant,
  AdvanceFunnelRequest,
  BuyerAccessItem,
  BuyerEngagementDetail,
  FunnelStage,
  UpdateGrantExpiryRequest,
} from '@dataroom/shared';
import { BuyerEngagementSection } from '@/components/BuyerEngagementSection';
import { Modal } from '@/components/Modal';

interface BuyerDetailModalProps {
  buyer: BuyerAccessItem;
  /** THIS buyer's access grants (the page filters the room list to their actorId). */
  grants: AccessGrant[];
  onClose: () => void;
  /** Advance one legal step. For the dated steps the modal supplies `date`. */
  onAdvance: (actorId: string, body: AdvanceFunnelRequest) => void;
  /** Move back one legal step (no body). */
  onRollback: (actorId: string) => void;
  advancing: boolean;
  rollingBack: boolean;
  /** Set a new (future) end date for a grant. */
  onUpdateExpiry: (grantId: string, body: UpdateGrantExpiryRequest) => void;
  onRevoke: (grantId: string) => void;
  /** Grant id whose expiry is being updated (disables its controls). */
  updatingExpiryId: string | null;
  /** Grant id being revoked (disables its control). */
  revokingId: string | null;
  /** Message from a failed action (e.g. 409 rollback-at-teaser / revoked grant). */
  actionError: string | null;
  onDismissError: () => void;
  /** ADR-0038 engagement for THIS buyer (the page owns the query). The Activity
   * section is shown only when the page passes this state. */
  engagement?: {
    data: BuyerEngagementDetail | undefined;
    loading: boolean;
    error: string | null;
  };
  /** The buyer's named team (the page renders BuyerTeamSection with its own queries). */
  team?: ReactNode;
}

/** The buyer's current access level in one plain phrase, from the funnel gates —
 * the same derivation the CIM overview row uses. */
function accessLevel(b: BuyerAccessItem): string {
  // Reaching due diligence opens what the admin grants — not every file (ADR-0010 §2).
  if (b.allFilesUnlocked) return 'Due diligence — as granted';
  if (b.cimUnlocked) return 'CIM unlocked';
  if (b.ndaSigned) return 'NDA signed';
  return 'Teaser only';
}

function fmtDate(iso: string | null): string {
  return iso ? formatDate(iso) : '—';
}

/** A dated stage needs an entry date; the plain stages advance in one click. */
function isDatedStage(stage: FunnelStage): boolean {
  return stage === 'exclusivity_expires' || stage === 'closing';
}

/** Stages the buyer reaches by SIGNING (the NDA, their LOI). The admin can only
 * move them there as an audited override with a reason (the API 409s otherwise). */
function isSignatureStage(stage: FunnelStage): boolean {
  return stage === 'nda_signed' || stage === 'loi_submitted';
}

/** A short human summary of a grant's tag-area scope. */
function scopeSummary(g: AccessGrant): string {
  const n = g.tagAreaScope.length;
  const base = n === 0 ? 'No tag areas' : `${n} tag area${n === 1 ? '' : 's'}`;
  const excl = g.excludedTagIds.length > 0 ? `, ${g.excludedTagIds.length} excluded` : '';
  return base + excl;
}

/** Per-grant end-date editor: extend (or shorten) a buyer's access to a new date.
 * There is no "ongoing" here — a buyer's access always ends (owner rule
 * 2026-10-21). Presentational — the parent owns the mutation. Live grants only. */
function GrantExpiryEditor({
  grant,
  pending,
  onUpdateExpiry,
}: {
  grant: AccessGrant;
  pending: boolean;
  onUpdateExpiry: (grantId: string, body: UpdateGrantExpiryRequest) => void;
}) {
  const [end, setEnd] = useState<AccessEndValue>(defaultAccessEnd);
  // A date picker yields a calendar date; the API stores an INSTANT and grants
  // access while expires_at > now(). "Until <date>" means access through the END
  // of that day in the admin's own time zone (UTC end-of-day cut US admins'
  // buyers off ~7 pm the evening before) — `accessEndIso` does that.
  const expiresAt = accessEndIso(end);
  return (
    <div className="space-y-1.5">
      <AccessEndPicker
        value={end}
        onChange={setEnd}
        disabled={pending}
        legend={grant.ongoing ? 'Set an end date' : 'Change the end date'}
      />
      <Button
        variant="outline"
        size="sm"
        disabled={pending || !expiresAt}
        onClick={() => expiresAt && onUpdateExpiry(grant.id, { expiresAt })}
      >
        {pending ? '…' : 'Set expiry'}
      </Button>
    </div>
  );
}

/**
 * PRD-0004 §3e buyer-detail — the per-buyer action panel opened from a CIM-access
 * row. Shows the buyer's email, funnel stage, and current access level, plus the
 * buyer's access grants, and lets the admin: advance / roll back the funnel stage,
 * change or clear each grant's expiry, and revoke a grant. With `engagement` it
 * also shows the buyer's Activity (ADR-0038). Presentational — the page owns the
 * mutations, the engagement query and query invalidation.
 */
export function BuyerDetailModal({
  buyer,
  grants,
  onClose,
  onAdvance,
  onRollback,
  advancing,
  rollingBack,
  onUpdateExpiry,
  onRevoke,
  updatingExpiryId,
  revokingId,
  actionError,
  onDismissError,
  engagement,
  team,
}: BuyerDetailModalProps) {
  const [excDate, setExcDate] = useState('');
  // The audited-override step for the signature-gated stages.
  const [overriding, setOverriding] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');
  const reasonId = useId();
  // A new stage (after an advance/roll-back) starts with the override closed.
  useEffect(() => {
    setOverriding(false);
    setOverrideReason('');
  }, [buyer.stage]);
  const idx = FUNNEL_STAGES.indexOf(buyer.stage);
  const next = idx >= 0 && idx < FUNNEL_STAGES.length - 1 ? FUNNEL_STAGES[idx + 1]! : null;
  const previous = idx > 0 ? FUNNEL_STAGES[idx - 1]! : null;
  const busy = advancing || rollingBack;

  return (
    <Modal
      title="Buyer access"
      description={buyer.actorEmail ?? buyer.actorId}
      onClose={onClose}
      size={engagement ? 'lg' : 'md'}
      footer={
        <Button variant="outline" size="sm" onClick={onClose}>
          Close
        </Button>
      }
    >
      <div className="space-y-5">
        {actionError ? (
          <div
            role="alert"
            className="flex items-start justify-between gap-3 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive"
          >
            <span>{actionError}</span>
            <button type="button" onClick={onDismissError} className="text-xs font-medium underline">
              Dismiss
            </button>
          </div>
        ) : null}

        {/* Current position. */}
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <p className="text-xs font-medium text-muted-foreground">Funnel stage</p>
            <p className="font-medium">{FUNNEL_STAGE_LABELS[buyer.stage]}</p>
          </div>
          <div>
            <p className="text-xs font-medium text-muted-foreground">Current access</p>
            <p className="font-medium">{accessLevel(buyer)}</p>
          </div>
        </div>

        {team}

        {engagement ? (
          <BuyerEngagementSection
            engagement={engagement.data}
            loading={engagement.loading}
            error={engagement.error}
          />
        ) : null}

        {/* Funnel controls: advance / roll back one legal step. */}
        <div className="space-y-2 rounded-md border bg-muted/30 p-3">
          <div className="flex items-center gap-1">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Funnel stage
            </p>
            <HelpTip id="admin.buyers.funnel-override" className="ml-1" />
          </div>
          <p className="text-xs text-muted-foreground">
            Rolling a buyer back removes any access their higher stage had unlocked — they can no
            longer reach what that stage opened.
          </p>
          <div className="flex flex-wrap items-end gap-2">
            {previous ? (
              <Button
                variant="outline"
                size="sm"
                disabled={busy}
                onClick={() => onRollback(buyer.actorId)}
              >
                {rollingBack ? 'Moving back…' : `Move back to ${FUNNEL_STAGE_LABELS[previous]}`}
              </Button>
            ) : (
              <span className="text-xs text-muted-foreground">At the first stage.</span>
            )}

            {next ? (
              isDatedStage(next) ? (
                <div className="flex items-end gap-1.5">
                  <div>
                    <p className="text-xs font-medium text-muted-foreground">
                      {FUNNEL_STAGE_LABELS[next]} date
                    </p>
                    <input
                      type="date"
                      value={excDate}
                      disabled={busy}
                      onChange={(e) => setExcDate(e.target.value)}
                      className="rounded border px-1.5 py-0.5 text-xs"
                      aria-label={`${FUNNEL_STAGE_LABELS[next]} date`}
                    />
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={busy || !excDate}
                    onClick={() => onAdvance(buyer.actorId, { to: next, date: excDate })}
                  >
                    {advancing ? '…' : `Advance to ${FUNNEL_STAGE_LABELS[next]}`}
                  </Button>
                </div>
              ) : isSignatureStage(next) ? (
                // The buyer reaches this stage by SIGNING. Moving them without
                // the signature is an audited override that needs a reason.
                overriding ? null : (
                  <Button variant="outline" size="sm" disabled={busy} onClick={() => setOverriding(true)}>
                    {`Override: move to ${FUNNEL_STAGE_LABELS[next]}…`}
                  </Button>
                )
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={busy}
                  onClick={() => onAdvance(buyer.actorId, { to: next })}
                >
                  {advancing ? 'Advancing…' : `Advance to ${FUNNEL_STAGE_LABELS[next]}`}
                </Button>
              )
            ) : (
              <span className="text-xs text-muted-foreground">At the final stage.</span>
            )}
          </div>
          {next && isSignatureStage(next) && !overriding ? (
            <p className="text-xs text-muted-foreground">
              {next === 'nda_signed'
                ? 'The buyer moves to “NDA signed” when they sign the NDA you issue under Agreements.'
                : 'The buyer moves to “LOI submitted” when they sign their letter of intent.'}
            </p>
          ) : null}
          {next && isSignatureStage(next) && overriding ? (
            <div className="space-y-2 rounded-md border border-st-check-line bg-st-check p-3">
              <p className="text-sm font-medium text-st-check-fg">
                Override (record reason): move to {FUNNEL_STAGE_LABELS[next]} without a signature
              </p>
              <p className="text-xs text-st-check-fg">
                {next === 'nda_signed'
                  ? 'This bypasses the buyer’s NDA signature and unlocks what “NDA signed” opens. '
                  : 'This records the LOI stage without the buyer’s signed letter of intent. '}
                It is recorded in the room’s audit log as an override, with your reason.
              </p>
              <label className="block text-xs font-medium" htmlFor={reasonId}>
                Reason (required)
              </label>
              <textarea
                id={reasonId}
                value={overrideReason}
                disabled={busy}
                onChange={(e) => setOverrideReason(e.target.value)}
                rows={2}
                maxLength={1000}
                className="w-full rounded border border-input bg-background px-2 py-1 text-sm"
                placeholder="e.g. NDA signed on paper on 1 Oct; scanned copy in Agreements."
              />
              <div className="flex gap-2">
                <Button
                  size="sm"
                  disabled={busy || overrideReason.trim().length === 0}
                  onClick={() =>
                    onAdvance(buyer.actorId, {
                      to: next,
                      override: true,
                      reason: overrideReason.trim(),
                    })
                  }
                >
                  {advancing ? 'Recording…' : 'Record override & move'}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={busy}
                  onClick={() => {
                    setOverriding(false);
                    setOverrideReason('');
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : null}
        </div>

        {/* This buyer's access grants. */}
        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Access grants
          </p>
          {grants.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              This buyer has no access grants in this room.
            </p>
          ) : (
            <ul className="space-y-3">
              {grants.map((g) => {
                const revoked = g.revokedAt != null;
                const perms = [
                  'View',
                  g.downloadAllowed ? 'Download' : null,
                  g.writeAllowed ? 'Write' : null,
                ].filter(Boolean) as string[];
                return (
                  <li key={g.id} className="rounded-md border p-3 text-sm">
                    <div className="flex items-start justify-between gap-3">
                      <div className="space-y-0.5">
                        <p className="font-medium">
                          {g.role ? ACCESS_ROLE_LABELS[g.role] : 'Grant'}
                        </p>
                        <p className="text-xs text-muted-foreground">{scopeSummary(g)}</p>
                        <p className="text-xs text-muted-foreground">
                          {perms.join(' · ')}
                          {g.funnelStage
                            ? ` · Gated: ${FUNNEL_STAGE_LABELS[g.funnelStage]}`
                            : ''}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {g.ongoing
                            ? 'Ongoing — set an end date below'
                            : `Until ${fmtDate(g.expiresAt)}`}
                        </p>
                      </div>
                      <span
                        className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${
                          revoked
                            ? 'bg-destructive/10 text-destructive'
                            : 'bg-st-certified text-st-certified-fg'
                        }`}
                      >
                        {revoked ? 'Revoked' : 'Live'}
                      </span>
                    </div>

                    {!revoked ? (
                      <div className="mt-2 flex flex-wrap items-center justify-between gap-2 border-t pt-2">
                        <GrantExpiryEditor
                          grant={g}
                          pending={updatingExpiryId === g.id}
                          onUpdateExpiry={onUpdateExpiry}
                        />
                        <button
                          type="button"
                          disabled={revokingId === g.id}
                          onClick={() => onRevoke(g.id)}
                          className="text-xs font-medium text-destructive hover:underline disabled:opacity-50"
                        >
                          {revokingId === g.id ? 'Revoking…' : 'Revoke'}
                        </button>
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </Modal>
  );
}
